<template>
  <span style="margin-left: 1rem;" v-if="!onlyShowAddCommentInput && commentsData && commentsData.length > 0 && showTitle">{{ $t('announcement_latest_comments') }}</span>
  <div class="announcement-comments_data" v-if="!onlyShowAddCommentInput && commentsData && commentsData.length > 0">
    <div
      class="announcement-comment"
      v-for="(comment, index) of commentsData.slice(0, commentsLimit).reverse()"
      :key="'comment' + index"
    >
      <span
        :class="{'clickable': canStartConversation(comment.user)}"
        @click="startConversation(comment.user, 'announcementHeader.startConversationFromComment')"
      >
        <div
          class="announcement-new-comment__image"
          v-tooltip.right="(canStartConversation(comment.user) ? 'Start conversation with ' + comment.user.name : (comment.user.id === userId ? 'You': ''))"
          :style="{backgroundImage: `url(${comment.user.photo_url ?? comment.user.photoUrl})`}"
        />
      </span>
      <div class="talkbubble">
        <div style="display: flex; width: 100%">
          <div class="announcement-comment__user">
            <strong>{{ comment.user.name }}</strong>
          </div>
          <div class="announcement-comment__date">
            {{ fromNow(comment.created_at) }}
          </div>
        </div>
        <div class="announcement-comment__text" style="max-width: 500px;">
          <rich-body-content
            :html="comment.comment"
            style="margin-bottom: unset; text-wrap: wrap; max-width: 500px; word-break: break-word; overflow-wrap: anywhere;
  "
          ></rich-body-content>
        </div>
      </div>
      <div class="comment-button">
        <ds-button
          v-if="editable(comment)"
          @click="deleteComment(comment)"
          class="button--minimal button--default"
          icon="trash"
          size="small"
          iconSize="18"
        />
      </div>
    </div>
  </div>

  <div v-if="isLoggedIn && canComment" class="announcement-detail-comments">
    <div class="announcement-comment">
      <div
        class="announcement-new-comment__image"
        :style="{backgroundImage: `url(${$store.state.user.profile.photo_url})`}"
      />
      <div
        v-if="newCommentSuccess"
        class="talkbubble success"
      >
        <div class="announcement-comment__text">
          {{ $t('announcement_new_comment_thank_you') }}&nbsp;&nbsp;
          <ds-button
            size="extra-small"
            @click="newCommentSuccess = false"
            variant="rounded"
            :label="$t('announcement_add_new_comment')"
          />
        </div>
      </div>
      <form-group
        v-else
        label="hidden"
        class="announcement-new-comment_form"
        style="width: 100%;"
        :errors="errors.comment"
      >
        <div class="talkbubble" style="width: 100%;">
          <rich-text-area
            style="max-width: 100%;"
            class="newcomment__input"
            :key="'newcomment-' + key"
            v-model="newComment"
            :is-simplified="true"
            :hide-actions="true"
            :auto-focus="shouldScrollToComments"
            :currentContentLength="commentBodyWithoutHtml.length"
            :also-allow-user-mentions="true"
            :community-id="communityId"
            :custom-placeholder="$t('comment_placeholder')"
          />
          <ds-button
            @click="addComment()"
            variant="rounded"
            :icon="loadingNewComment ? '' : 'paper-plane'"
            icon-fill-color="white"
            size="small"
          />
        </div>
      </form-group>
    </div>
  </div>
  <div v-else-if="canComment">
    <div class="announcement-comment" @click="openLoginModal">
      <div
        class="announcement-new-comment__image"
        :style="{backgroundImage: `url(/images/default-avatar.png)`}"
      />

      <form-group
        label="hidden"
        class="announcement-new-comment_form"
        style="width: 100%;"
        :errors="errors.comment"
      >
        <div class="talkbubble" style="width: 100%;">
          <p>{{ $t('comment_placeholder') }}</p>
        </div>
      </form-group>
    </div>
  </div>
</template>
<script>
  import { defineComponent } from 'vue'
  import RichBodyContent from '@/components/RichBodyContent/RichBodyContent.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import { ConversationsMixin } from '@/util/ConversationsMixin'
  import { trackHeapEvent } from '@/util/analytics'
  import { Conversations } from '@/api/conversations'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import UserMixin from '@/util/UserMixin'
  import { fromNow } from '../util/date'
  import RichTextArea from '@/components/TextArea/RichTextArea.vue'
  import _isEmpty from 'lodash/isEmpty'
  import { deleteComment } from '@/api/notifications'
  import { deleteChallengeComment } from '@/Domain/Challenge/Api/challenges'

  export default defineComponent({
    name: 'Comments',
    components: { RichTextArea, RichBodyContent },
    mixins: [ConversationsMixin, UserMixin],
    emits: ['add-comment', 'delete-comment'],
    props: {
      comments: {
        type: Array,
        default: () => [],
      },
      key: {
        type: String,
      },
      shouldScrollToComments: {
        type: Boolean,
        default: false,
      },
      communityId: {
        type: Number,
        default: null
      },
      commentsLimit: {
        type: Number,
        default: 2,
      },
      resourceId: {
        type: String,
      },
      showTitle: {
        type: Boolean,
        required: false,
        default: true
      },
      canComment: {
        type: Boolean,
        required: false,
        default: true,
      },
      onlyShowAddCommentInput: {
        type: Boolean,
        required: false,
      }
    },
    computed: {
      commentBodyWithoutHtml () {
        return (this.newComment && this.newComment.replace(/<\/?[^>]+(>|$)/g, '')) || ''
      },
      commentsData () {
        if (this.comments && this.comments.length) {
          return Array.isArray(this.comments) ? [...this.comments].sort((a, b) => b.id - a.id) : []
        }

        return []
      },
      commentsAreLoaded () {
        return Array.isArray(this.comments)
      },
    },
    data () {
      return {
        loadingNewComment: false,
        newCommentSuccess: false,
        newComment: '',
        errors: {},
        commentsIconKey: 0,
      }
    },
    mounted: function () {
      this.onCommentsChange()

      this.$bus.on('commentDeleteConfirmation', (context) => {
        if (context.resource) {
          if (context.resource.notification_id) {
            if (context.resource.notification_id === this.resourceId) {
              this.confirmDeleteComment(context.resource)
            }
          }
        }

        if (context.resource && context.resource.challenge_id && context.resource.challenge_id === Number(this.resourceId)) {
          this.confirmDeleteChallengeComment(context.resource)
        }
      })
    },
    beforeUnmount () {
      this.$bus.off('commentDeleteConfirmation')
    },
    methods: {
      openLoginModal () {
        if (!this.isLoggedIn) {
          return this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
        }
      },
      fromNow,
      validateForm () {
        this.errors = {}

        if (!this.newComment || this.commentBodyWithoutHtml.length === 0) {
          this.errors = { comment: [this.$t('announcement_comment_error_required')] }
        }
      },
      addComment () {
        if (!this.isLoggedIn) {
          return this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
        }

        this.validateForm()

        if (!_isEmpty(this.errors) || !this.newComment) {
          return
        }

        this.loadingNewComment = true
        this.commentsIconKey++
        this.$emit('add-comment', this.newComment)
        this.newCommentSuccess = true
      },
      canStartConversation (user) {
        if (!this.isLoggedIn) {
          return false
        }

        if (!this.hasAccessToConversations) {
          return false
        }

        if (user && user.is_deleted) {
          return false
        }

        if (user && user.user_is_deleted) {
          return false
        }

        if (user.id === this.userId) {
          return false
        }

        return this.$store.getters.canActorsBeContacted || this.isOwner
      },
      editable (comment) {
        if (!this.$store.state.user || !this.$store.state.user.profile) {
          return false
        }

        return this.isOwner || comment.user_id === this.$store.state.user.profile.id
      },
      startConversation (user, trackHeap = 'announcementDetail.clickConversation') {
        if (!this.canStartConversation(user) || this.userId === user.id) {
          return
        }

        trackHeapEvent(trackHeap)

        Conversations.post({ recipient_id: user.id }).then((conversation) => {
          this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          this.$router.push('/profile/conversations/' + conversation.id).then(() => {
            this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
              component: 'user-information-side-panel',
              metaData: {
                userId: user.id,
                allowDelete: false,
              },
            })
          })
        })
      },
      onCommentsChange () {
        if (this.comments && this.commentsAreLoaded) {
          this.loadingNewComment = false
          this.commentsIconKey++
          this.newComment = ''
          if (this.shouldScrollToComments && !this.hasScrolledToComments) {
            setTimeout(() => {
              this.$refs.comments.scrollIntoView({ behavior: 'smooth' })
              this.hasScrolledToComments = true
            })
          }
        }
      },
      deleteComment (comment) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          name: 'comment',
          resource: comment,
          modalContextType: 'comment',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      confirmDeleteComment (comment) {
        deleteComment(this.resourceId, comment.id)
          .then(() => {
            this.$emit('delete-comment')
          })
      },
      confirmDeleteChallengeComment (comment) {
        deleteChallengeComment(Number(this.resourceId), comment.id)
          .then(() => {
            this.$emit('delete-comment')
          })
      }
    },
    watch: {
      comments () {
        this.onCommentsChange()
      }
    },
  })
</script>


<style scoped lang="scss">
  .form-group {
    margin-bottom: 0 !important;
  }
</style>
